import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileText, Trash2, Upload } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { DocumentUploadModal } from '../../components/DocumentUploadModal';
import { useToast } from '../../components/toast';
import { Badge, Button, Card, ConfirmDialog, EmptyState, ErrorState, Input, PageHeader, Pagination, Select, Spinner, Table, Td, Th } from '../../components/ui';
import { api, errorMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import {
  DOCUMENT_KIND_LABEL,
  documentUrl,
  equipmentLabel,
  formatBytes,
  INLINE_DOCUMENT_TYPES,
  useFacilityPlaces,
  type DocumentPage,
  type FacilityDocument,
  type FacilityDocumentKind,
} from '../../lib/facilities';
import { formatDate } from '../../lib/format';
import { useDebouncedValue, useUrlState } from '../../lib/hooks';

const PAGE_SIZE = 25;
const DEFAULTS = { search: '', kind: '', buildingId: '', page: '1' };

function LinkedTo({ document }: { document: FacilityDocument }) {
  const rows: { label: string; value: ReactNode }[] = [];
  if (document.facilityAsset) {
    rows.push({
      label: 'Equipment',
      value: (
        <Link to={`/facilities/equipment/${document.facilityAsset.id}`} className="text-brand-700 hover:underline">
          {equipmentLabel(document.facilityAsset)}
        </Link>
      ),
    });
  }
  if (document.building) rows.push({ label: 'Building', value: document.building.name });
  if (document.inspection) {
    rows.push({
      label: 'Inspection',
      value: (
        <Link to={`/facilities/inspections/${document.inspection.id}`} className="text-brand-700 hover:underline">
          {document.inspection.name}
        </Link>
      ),
    });
  }
  if (document.workOrder) {
    rows.push({
      label: 'Work order',
      value: (
        <Link to={`/facilities/work-orders/${document.workOrder.id}`} className="text-brand-700 hover:underline">
          #{document.workOrder.number} {document.workOrder.title}
        </Link>
      ),
    });
  }
  if (rows.length === 0) return <span className="text-slate-400">—</span>;
  return (
    <div className="space-y-0.5">
      {rows.map((row) => (
        <p key={row.label} className="truncate">
          <span className="text-xs text-slate-400">{row.label} </span>
          {row.value}
        </p>
      ))}
    </div>
  );
}

export function DocumentsPage() {
  const { me } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useUrlState(DEFAULTS);
  const [searchInput, setSearchInput] = useState(filters.search);
  const search = useDebouncedValue(searchInput, 300);
  const places = useFacilityPlaces();
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState<FacilityDocument | null>(null);

  useEffect(() => {
    if (search !== filters.search) setFilters({ search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const page = Number(filters.page) || 1;
  const query = useQuery({
    queryKey: ['facilities', 'documents', filters],
    queryFn: () =>
      api.get<DocumentPage>('/facilities/documents', {
        search: filters.search,
        kind: filters.kind,
        buildingId: filters.buildingId,
        page,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  const remove = useMutation({
    mutationFn: (document: FacilityDocument) => api.delete(`/facilities/documents/${document.id}`),
    onSuccess: (_, document) => {
      void queryClient.invalidateQueries({ queryKey: ['facilities'] });
      toast.success(`Deleted ${document.title}`);
      setDeleting(null);
    },
    onError: (err) => {
      setDeleting(null);
      toast.error(errorMessage(err));
    },
  });

  const permissions = query.data?.permissions;
  const canUpload = !!permissions?.upload;
  const canDelete = (document: FacilityDocument) =>
    !!permissions && (permissions.deleteAny || (permissions.upload && !!me && document.uploadedById === me.user.id));
  const hasFilters = !!(filters.search || filters.kind || filters.buildingId);

  return (
    <>
      <PageHeader
        title="Documents"
        description="Manuals, warranties, maintenance logs, and inspection reports in one searchable place"
        actions={
          canUpload && (
            <Button variant="primary" icon={<Upload />} onClick={() => setUploading(true)}>
              Upload document
            </Button>
          )
        }
      />

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-3">
          <Input
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search titles, files, tags, equipment, buildings, or work order #"
            title="Matches titles, descriptions, file names, exact tags, equipment, buildings, inspections, and work order numbers"
            className="w-full sm:w-96"
          />
          <Select value={filters.kind} onChange={(event) => setFilters({ kind: event.target.value })} className="w-auto">
            <option value="">All types</option>
            {(Object.keys(DOCUMENT_KIND_LABEL) as FacilityDocumentKind[]).map((value) => (
              <option key={value} value={value}>
                {DOCUMENT_KIND_LABEL[value]}
              </option>
            ))}
          </Select>
          <Select value={filters.buildingId} onChange={(event) => setFilters({ buildingId: event.target.value })} className="w-auto">
            <option value="">All buildings</option>
            {places.data?.map((building) => (
              <option key={building.id} value={building.id}>
                {building.name}
              </option>
            ))}
          </Select>
          {hasFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchInput('');
                setFilters({ search: '', kind: '', buildingId: '' });
              }}
            >
              Clear filters
            </Button>
          )}
          {query.isFetching && !query.isLoading && <Spinner className="size-4" />}
        </div>

        {query.isLoading ? (
          <div className="flex h-60 items-center justify-center">
            <Spinner />
          </div>
        ) : query.error || !query.data ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : query.data.items.length === 0 ? (
          <EmptyState
            icon={<FileText />}
            title={hasFilters ? 'No documents match these filters' : 'No documents yet'}
            description={
              hasFilters
                ? 'Try a different search or clear the filters. Tags match exactly.'
                : 'Upload manuals, warranties, maintenance logs, and inspection reports so the team can find them when they need them.'
            }
            action={
              !hasFilters &&
              canUpload && (
                <Button variant="primary" icon={<Upload />} onClick={() => setUploading(true)}>
                  Upload document
                </Button>
              )
            }
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Document</Th>
                  <Th className="hidden sm:table-cell">Type</Th>
                  <Th className="hidden lg:table-cell">Tags</Th>
                  <Th className="hidden md:table-cell">Linked to</Th>
                  <Th className="hidden lg:table-cell">Uploaded</Th>
                  <Th className="text-right">
                    <span className="sr-only">Actions</span>
                  </Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {query.data.items.map((document) => {
                  const viewable = INLINE_DOCUMENT_TYPES.has(document.contentType);
                  return (
                    <tr key={document.id}>
                      <Td className="max-w-sm">
                        <a
                          href={documentUrl(document, true)}
                          target={viewable ? '_blank' : undefined}
                          rel="noreferrer"
                          className="block truncate font-medium text-slate-900 hover:text-brand-700"
                        >
                          {document.title}
                        </a>
                        <p className="truncate text-xs text-slate-500">
                          {document.fileName} · {formatBytes(document.sizeBytes)}
                        </p>
                        {document.description && <p className="truncate text-xs text-slate-500">{document.description}</p>}
                      </Td>
                      <Td className="hidden sm:table-cell">
                        <Badge>{DOCUMENT_KIND_LABEL[document.kind]}</Badge>
                      </Td>
                      <Td className="hidden lg:table-cell">
                        {document.tags.length > 0 ? (
                          <div className="flex max-w-[12rem] flex-wrap gap-1">
                            {document.tags.map((tag) => (
                              <button
                                key={tag}
                                type="button"
                                onClick={() => setSearchInput(tag)}
                                title={`Show documents tagged ${tag}`}
                                className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-brand-50 hover:text-brand-800"
                              >
                                {tag}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </Td>
                      <Td className="hidden max-w-[16rem] md:table-cell">
                        <LinkedTo document={document} />
                      </Td>
                      <Td className="hidden whitespace-nowrap lg:table-cell">
                        {document.uploadedByName}
                        <p className="text-xs text-slate-500">{formatDate(document.createdAt)}</p>
                      </Td>
                      <Td className="text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1">
                          <a
                            href={documentUrl(document)}
                            title="Download"
                            aria-label={`Download ${document.title}`}
                            className="inline-flex size-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                          >
                            <Download className="size-4" />
                          </a>
                          {canDelete(document) && (
                            <Button size="sm" variant="ghost" icon={<Trash2 />} title="Delete" aria-label={`Delete ${document.title}`} onClick={() => setDeleting(document)} />
                          )}
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <Pagination page={page} pageSize={PAGE_SIZE} total={query.data.total} onPage={(next) => setFilters({ page: String(next) }, { resetPage: false })} />
          </>
        )}
      </Card>

      <DocumentUploadModal open={uploading} onClose={() => setUploading(false)} />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting)}
        loading={remove.isPending}
        title={deleting ? `Delete “${deleting.title}”?` : 'Delete document?'}
        description="This permanently deletes the file for everyone."
        confirmLabel="Delete document"
      />
    </>
  );
}
